import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "components";
import startCase from "lodash/startCase";
import { useCallback, useEffect, useRef, useState } from "react";
import { getWorkatoToken } from "../IntegrationSettings.requests";
import {
  WorkatoConnection,
  WorkatoEvent,
  WorkatoEventType,
} from "../IntegrationSettings.types";

const IFRAME_DEFAULT_HEIGHT = 500;
const IFRAME_LOAD_TIMEOUT_MS = 5000;
const MAX_RETRIES = 3;

const IntegrationConnection = ({
  companyId,
  workatoCustomerId,
  connection: { id, provider, name },
}: {
  companyId: number;
  workatoCustomerId: number;
  connection: WorkatoConnection;
}) => {
  const [isConnected, setConnected] = useState<boolean>(false);
  const [iframeHeight, setIframeHeight] = useState<number>(
    IFRAME_DEFAULT_HEIGHT
  );
  const [connectionStatusLoading, setConnectionStatusLoading] =
    useState<boolean>(true);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeLoadedRef = useRef(false);
  const retryCountRef = useRef(0);

  const { data: token, refetch: refetchToken } = useQuery(
    ["getWorkatoToken", companyId, workatoCustomerId, id],
    async () =>
      getWorkatoToken({
        companyId,
        workatoCustomerId: Number(workatoCustomerId),
      }),
    { enabled: Boolean(workatoCustomerId), cacheTime: 0, staleTime: 0 }
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    iframeLoadedRef.current = false;

    const timeout = setTimeout(() => {
      if (!iframeLoadedRef.current && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        refetchToken().then(() => setIframeKey((k) => k + 1));
      }
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [token, iframeKey, refetchToken]);

  const handleOnReceiveMessage = useCallback(
    (event: { data: string }) => {
      if (!(typeof event?.data === "string")) {
        return;
      }

      let data: WorkatoEvent;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const { type, payload } = data;
      switch (true) {
        case type === WorkatoEventType.CONNECTION_STATUS_CHANGE &&
          provider === payload.provider:
          iframeLoadedRef.current = true;
          setConnected(Boolean(payload.connected));
          setConnectionStatusLoading(false);
          break;
        case type === WorkatoEventType.ERROR:
          console.error("Workato error:", payload.message);
          break;
        case type === WorkatoEventType.HEIGHT_CHANGE:
          setIframeHeight(payload.height ?? IFRAME_DEFAULT_HEIGHT);
          break;
        default:
          // eslint-disable-next-line no-console
          console.log("Unsupported Workato Message:", { type, payload });
          break;
      }
    },
    [provider]
  );

  useEffect(() => {
    window.addEventListener("message", handleOnReceiveMessage);

    return () => {
      window.removeEventListener("message", handleOnReceiveMessage);
    };
  }, [handleOnReceiveMessage]);

  return (
    <AccordionItem key={`${name}_${provider}`}>
      <h2>
        <AccordionButton>
          <Avatar
            src={`https://intentsify-email-assets.s3.us-west-1.amazonaws.com/${provider}.svg`}
          />
          <Box flex="1" textAlign="left" ml={5}>
            <HStack maxWidth={600}>
              <SimpleGrid columns={2}>
                <Box w="95px">
                  <Text fontWeight="bold">
                    {startCase(provider.toLowerCase())}
                  </Text>
                </Box>
                <Box w="300px">
                  <Text color="gray.500" fontSize="sm">
                    {name}
                  </Text>
                </Box>
              </SimpleGrid>
            </HStack>
          </Box>
          <Box mr={5}>
            {connectionStatusLoading ? (
              <Flex>
                <Spinner ml={3} size="sm" />
              </Flex>
            ) : (
              <Badge colorScheme={isConnected ? "green" : "red"}>
                {isConnected ? "Connected" : "Not Connected"}
              </Badge>
            )}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel pb={4} minHeight={1000}>
        <Box p={4} bgColor={"white"} borderRadius={4}>
          {token ? (
            <iframe
              key={iframeKey}
              id={`workato_${provider}_${name}`}
              width={"100%"}
              height={iframeHeight}
              src={`https://www.workato.com/direct_link/embedded/connections/${id}?workato_dl_token=${token}`}
            />
          ) : (
            <Flex justify="center" py={10}>
              <Spinner size="lg" />
            </Flex>
          )}
        </Box>
      </AccordionPanel>
    </AccordionItem>
  );
};

export { IntegrationConnection };
